import ComponentState, { ComponentStateTypes } from "@/components/common/ComponentState";
import NoData from "@/components/common/NoData";
import PropertyView from "@/components/common/views/PropertyView";
import { api } from "@/config/axios-context";
import { AccommodationMonthlyStatsModel } from "@/types/acommodation/acc-stats-types";
import useAppContext from "@/utilities/hooks/useAppContext";
import { useEffect, useState } from "react";
import AppStackedBarChart from "@/components/common/charts/AppStackedBarChart";
import { useParams } from "react-router-dom";
import { URI_PARAMETER_KEYS } from "@/config/router";
import { AppColors } from "@/config/app-constants";

type AccommodationStatsMonthyProps = {
    seasonId?: number
}

const propertyViewClassName = `
w-full lg:w-auto
bg-neutral-100 dark:bg-neutral-900
rounded-lg
p-4
`;

const AccommodationStatsMonthly = (props: AccommodationStatsMonthyProps) => {
    const context = useAppContext();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    var params = useParams<{ [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: string }>();
    const [data, setData] = useState<AccommodationMonthlyStatsModel>({
        season: { id: 0, name: '', active: false },
        months: [],
        roomTypes: [],
        channels: []
    });

    useEffect(() => {
        loadData().then();
    }, [props.seasonId, params[URI_PARAMETER_KEYS.TARGET_SEASON_ID]]);

    const loadData = async () => {
        try {
            const response = await api.get<AccommodationMonthlyStatsModel>({
                url: 'AccommodationStates/StatesMonthly',
                params: {
                    seasonId: props.seasonId
                }
            });

            if (!response.success) {
                throw response.statusText;
            }

            const responseData = response.data;
            responseData.channels = [
                { id: 0, name: context.t('accommodation.stats.direct'), color: AppColors.SUNRISE },
                ...responseData.channels
            ]

            setData(responseData);
            setComponentState('');
        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('nodata');
            throw error;
        }
    }

    function dataToDataset() {
        const dataset = [
            {
                id: 'neto',
                label: context.t('agriculture.stats.neto'),
                data: data.months.map((item) => item.stats.neto.toFixed(2)),
                color: '#7dc8faB3'
            },
            {
                id: 'expenses',
                label: context.t('accommodation.stats.expenses'),
                data: data.months.map((item) => (item.stats.work.totalSum + item.stats.booking.channelsData.totalSum).toFixed(2)),
                color: '#fa9bafB3'
            }
        ]

        return dataset;
    }

    function nightsDataset() {
        const roomTypeDataset = data.roomTypes.map(roomType => {
            return {
                id: roomType.id,
                label: roomType.name,
                color: roomType.color ? roomType.color + 'B3' : undefined,
                data: data.months.map((item) => {
                    const roomTypeStats = item.stats.booking.roomTypeStats?.find(x => x.id === roomType.id);
                    if (!roomTypeStats) return 0;
                    return roomTypeStats.stats?.nights || item.stats.booking.nights;
                })
            }
        });

        return roomTypeDataset;
    }

    function cancellationsDataset() {
        const dataSet = [
            {
                id: 'cancellations',
                color: '#fa9bafB3',
                label: context.t('accommodation.stats.cancellations'),
                data: data.months.map((item) => {
                    return item.stats.booking.canceled.count
                }),
            }
        ]

        return dataSet;
    }

    function channelDataset() {
        const channelDataset = data.channels.map(channel => {
            return {
                id: channel.id,
                label: channel.name,
                color: channel.color ? channel.color + 'B3' : undefined,
                data: data.months.map((item) => {
                    const channelStats = item.stats.booking.channelsData.items?.find(x => x.id === channel.id);
                    if (!channelStats) return 0;
                    return channelStats.count;
                })
            }
        });

        return channelDataset;
    }

    function roomTypeDataset() {
        const roomTypeDataset = data.roomTypes.map(roomType => {
            return {
                id: roomType.id,
                label: roomType.name,
                color: roomType.color ? roomType.color + 'B3' : undefined,
                data: data.months.map((item) => {
                    const roomTypeStats = item.stats.booking.roomTypeStats?.find(x => x.id === roomType.id);
                    if (!roomTypeStats) return 0;
                    return roomTypeStats.stats?.count || item.stats.booking.count;
                })
            }
        });

        return roomTypeDataset;
    }

    function workDataset() {
        const dataSet = [
            {
                id: 'work-maintenance',
                color: '#fa9bafB3',
                label: context.t('work.types.maintenance'),
                data: data.months.map((item) => {
                    return item.stats.work.maintenanceTotalSum
                }),
            },
            {
                id: 'work-investment',
                color: '#7dc8faB3',
                label: context.t('work.types.investment'),
                data: data.months.map((item) => {
                    return item.stats.work.investmentTotalSum
                }),
            }
        ]

        return dataSet;
    }

    return (
        <div>
            <ComponentState state={componentState} >
                {data?.months?.length > 0 ? (
                    <div className="p-4 w-full flex flex-wrap gap-6">
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.profit"
                        >
                            <AppStackedBarChart
                                name="monthly-profit"
                                legend
                                data={{
                                    labels: data.months.map((item) => item.name),
                                    dataSet: dataToDataset(),
                                    totals: data.months.map((item) => item.stats.bruto.toFixed(2))
                                }} />
                        </PropertyView>
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.roomTypeReservations"
                        >
                            <AppStackedBarChart
                                name="monthly-room-type-stats"
                                legend
                                data={{
                                    labels: data.months.map((item) => item.name),
                                    dataSet: roomTypeDataset(),
                                    totals: data.months.map((item) => item.stats.booking.count)
                                }} />
                        </PropertyView>
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.channelReservations"
                        >
                            <AppStackedBarChart
                                name="monthly-channels-stats"
                                legend
                                data={{
                                    labels: data.months.map((item) => item.name),
                                    dataSet: channelDataset(),
                                    totals: data.months.map((item) => item.stats.booking.count)
                                }} />
                        </PropertyView>
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.nightsTotalAverage"
                        >
                            <AppStackedBarChart
                                name="monthly-nights-stats"
                                legend
                                data={{
                                    labels: data.months.map((item) => item.name),
                                    dataSet: nightsDataset(),
                                    totals: data.months.map((item) => item.stats.booking.nights)
                                }} />
                        </PropertyView>
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.cancellations"
                        >
                            <AppStackedBarChart
                                name="monthly-cancellations-stats"
                                legend
                                data={{
                                    labels: data.months.map((item) => item.name),
                                    dataSet: cancellationsDataset(),
                                    totals: data.months.map((item) => item.stats.booking.canceled.count)
                                }} />
                        </PropertyView>
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.workExpenses"
                        >
                            <AppStackedBarChart
                                name="monthly-work-stats"
                                legend
                                data={{
                                    labels: data.months.map((item) => item.name),
                                    dataSet: workDataset(),
                                    totals: data.months.map((item) => item.stats.work.totalSum)
                                }} />
                        </PropertyView>
                    </div>
                ) : (
                    <NoData />
                )}
            </ComponentState>
        </div >
    );
};

export default AccommodationStatsMonthly;
